import { forwardRef, useImperativeHandle, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { userService, type User } from '@/services/userService'

export interface ClearTOTPModalRef {
  open: (user: User) => void
}

interface ClearTOTPModalProps {
  onCleared?: () => void
}

/**
 * Asks before turning off another account's second factor.
 *
 * Once cleared, the password alone opens the account, so this should follow a
 * check of who is asking. A row icon that acted on the first click skipped
 * that moment entirely.
 */
const ClearTOTPModal = forwardRef<ClearTOTPModalRef, ClearTOTPModalProps>(({ onCleared }, ref) => {
  const { t } = useTranslation(['passwordReset', 'users'])
  const [isOpen, setIsOpen] = useState(false)
  const [target, setTarget] = useState<User | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')

  useImperativeHandle(ref, () => ({
    open: (user: User) => {
      setTarget(user)
      setError('')
      setIsOpen(true)
    },
  }))

  const clear = async () => {
    if (!target || isLoading) return
    setIsLoading(true)
    setError('')
    try {
      await userService.clearTOTP(target.id)
      setIsOpen(false)
      onCleared?.()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'errors.clearTOTPFailed'
      setError(message.startsWith('errors.') ? t(message, { ns: 'platformUsers', defaultValue: message }) : message)
    } finally {
      setIsLoading(false)
    }
  }

  const close = () => {
    if (!isLoading) setIsOpen(false)
  }

  if (!isOpen || !target) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/30 backdrop-blur-md">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
        {/* Header */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-amber-600">{t('clearTotp.heading', 'Clear 2FA')}</h2>
              <p className="text-sm text-gray-600 mt-1">
                {t('clearTotp.subtitle', 'For an account whose authenticator is lost')}
              </p>
            </div>
            <button
              onClick={close}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              disabled={isLoading}
              aria-label={t('owner.close', 'Close')}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3" role="alert">
              <svg className="w-5 h-5 text-red-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <div className="bg-gray-50 rounded-lg p-4">
            <p className="text-sm text-gray-600 mb-3">
              {t('clearTotp.intro', 'Turn off two-factor authentication for the following user:')}
            </p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">{t('users:table.account', 'Account')}:</span>
                <span className="text-sm text-gray-900">{target.account}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">{t('users:table.name', 'Name')}:</span>
                <span className="text-sm text-gray-900">{target.name}</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">{t('users:table.role', 'Role')}:</span>
                <span className="text-sm text-gray-900">{t(`users:roles.${target.platform_role}`, target.platform_role)}</span>
              </div>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <svg className="w-5 h-5 text-yellow-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              <div className="flex-1">
                <h4 className="text-sm font-medium text-yellow-800 mb-2">{t('clearTotp.warning', 'Before you continue')}</h4>
                <ul className="text-sm text-yellow-700 space-y-1 list-disc list-inside">
                  <li>{t('clearTotp.item1', 'Their password alone will open the account.')}</li>
                  <li>{t('clearTotp.item2', 'Only do this once you are sure who is asking.')}</li>
                  <li>{t('clearTotp.item3', 'If their role requires two-factor, they set it up again at their next sign-in.')}</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={close}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            disabled={isLoading}
          >
            {t('owner.cancel', 'Cancel')}
          </button>
          <button
            type="button"
            onClick={() => void clear()}
            className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
            disabled={isLoading}
          >
            {isLoading && (
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
            )}
            <span>{t('clearTotp.confirm', 'Clear 2FA')}</span>
          </button>
        </div>
      </div>
    </div>
  )
})

ClearTOTPModal.displayName = 'ClearTOTPModal'
export default ClearTOTPModal
